import { useContext, useEffect, useState } from 'react';

import {
  CashboxContext,
  ESignScanStatus,
  ETaxVariant,
  openHonestSignModal,
  useIsCashboxEnabled,
  ESignModalType,
} from 'cashbox';
import { LocalizationContext } from 'localization';
import { openConfirmDialog } from 'services/Confirmation';
import notificationService from 'services/notificationService';
import { SettingsContext, ITax } from 'settings';
import { EPaymentItemType, TShopItem, Combo } from 'types';
import { calcLimit } from 'utils';

import { IShoppingCartReturnValue } from './interfaces';

const getTaxVariant = (
  item: TShopItem,
  taxes: Array<ITax>
): ETaxVariant | null => {
  const settingsTax = taxes.find((tax) =>
    tax.entities?.includes(item.type)
  )?.taxVariant;

  if (item.type === 'TARIFF') {
    return settingsTax ?? null;
  }

  return item.original.tax_system ?? settingsTax ?? null;
};

export const useShoppingCart = (
  clientUUID?: string | null
): IShoppingCartReturnValue => {
  const { t } = useContext(LocalizationContext);

  const {
    state: {
      settings: { taxes, bookingStrictMode },
    },
  } = useContext(SettingsContext);
  const { validateHonestSign } = useContext(CashboxContext);
  const isCashboxEnabled = useIsCashboxEnabled();

  const [combo, setCombo] = useState<Combo | null>(null);
  const [items, setItems] = useState<Array<TShopItem>>([]);
  const [currentTaxVariant, setCurrentTaxVariant] =
    useState<ETaxVariant | null>(null);

  useEffect(() => {
    if (!items.length) {
      setCurrentTaxVariant(null);
    }
  }, [items.length]);

  useEffect(() => {
    setItems([]);
    setCombo(null);
  }, [clientUUID]);

  useEffect(() => {
    if (!combo) {
      setItems((prevState) => prevState.filter((item) => !item.comboId));
    }
  }, [combo]);

  const removeItem = async (index: number): Promise<void> => {
    const itemForRemove = items[index];

    if (itemForRemove?.type === 'GOOD' && itemForRemove.sign) {
      const signData = await openHonestSignModal(
        validateHonestSign,
        ESignModalType.REMOVE
      );

      if (signData.status === ESignScanStatus.CANCELED) return;

      if (
        !items.some(
          (item) => item.type === 'GOOD' && item.sign === signData.sign
        )
      ) {
        notificationService.info({
          t,
          type: 'warning',
          title: 'Нельзя удалить товар',
          message: 'Товара с такой маркировкой нет в корзине',
        });
      }

      return setItems(
        items.filter(
          (item) => !(item.type === 'GOOD' && item.sign === signData.sign)
        )
      );
    }

    setItems(items.filter((_, i) => index !== i));
  };

  const setItemAmount = (
    id: number,
    type: EPaymentItemType,
    amount: number
  ): void => {
    if (amount <= 0) {
      const i = items.findIndex((item) => item.id === id && item.type === type);
      removeItem(i);
      return;
    }

    const cartItemIndex = items.findIndex(
      (el) => el.id === id && el.type === type && !el.comboId
    );

    if (cartItemIndex < 0) {
      return;
    }

    const amountLimit = calcLimit(items[cartItemIndex]);

    if (amount > amountLimit) {
      return;
    }

    items[cartItemIndex].amount = amount;

    setItems([...items]);
  };

  const addItem = async (item: TShopItem): Promise<void> => {
    if (combo && combo.id === item.comboId) return;

    if (item.type === 'TARIFF') {
      if (bookingStrictMode && item.original.booked) {
        return notificationService.info({
          t,
          type: 'warning',
          title: t('shoppingCart.tariff.intersection'),
        });
      }

      if (!clientUUID && item.original.pausable) {
        return notificationService.info({
          t,
          type: 'warning',
          title: t('shoppingCart.tariff.onlyClient'),
        });
      }

      if (
        item.original.pausable &&
        items.some((cartItem) => cartItem.type === 'TARIFF')
      ) {
        return notificationService.info({
          t,
          type: 'warning',
          title: t('shoppingCart.tariff.pause'),
        });
      }

      if (
        item.type === 'TARIFF' &&
        items.some(
          (cartItem) => cartItem.type === 'TARIFF' && cartItem.original.pausable
        )
      ) {
        return notificationService.info({
          t,
          type: 'warning',
          title: t('shoppingCart.tariff.pause'),
        });
      }
    }

    if (isCashboxEnabled) {
      const newTaxVariant = getTaxVariant(item, taxes);

      if (items.length && newTaxVariant !== currentTaxVariant) {
        openConfirmDialog(t, 'notify', {
          title: 'Ошибка добавления в корзину',
          message:
            'В корзине уже есть элементы с другой системой налогообложения.\n\n Добавьте другую позицию или поменяйте систему налогообложения в настройках на вкладке касса',
        });
        return;
      }

      setCurrentTaxVariant(newTaxVariant);

      if (item.type === 'GOOD' && item.original.use_fair_sign) {
        const copiedItem = { ...item };
        if (!item.sign) {
          const signData = await openHonestSignModal(validateHonestSign);
          if (signData.status === ESignScanStatus.CANCELED) return;

          copiedItem.sign = signData.sign;
        } else {
          const isValid = await validateHonestSign(item.sign);

          if (!isValid.validate) {
            return notificationService.info({
              t,
              type: 'warning',
              title: isValid.errorMessage
                ? 'Некоректный честный знак'
                : 'Некорректный код',
              message:
                isValid.errorMessage ??
                'Отсканируйте маркировку "Честный ЗНАК"',
            });
          }
        }

        if (
          items.some(
            (cartItem) =>
              cartItem.type === 'GOOD' &&
              Boolean(copiedItem.sign) &&
              cartItem.sign === copiedItem.sign
          )
        ) {
          return notificationService.info({
            t,
            type: 'warning',
            title: 'Нельзя добавить товар',
            message: 'Товар с такой маркировкой уже находится в корзине',
          });
        }

        return setItems((prevState) => [...prevState, copiedItem]);
      }
    }

    const cartItem = items.find(
      (el) =>
        el.id === item.id &&
        item.type !== 'TARIFF' &&
        item.type === el.type &&
        !el.comboId
    );

    if (cartItem) {
      return setItemAmount(item.id, item.type, cartItem.amount + item.amount);
    }

    setItems((prevState) => [...prevState, item]);
  };

  const clearShoppingCart = (): void => {
    setItems([]);
  };

  return {
    items,
    combo,
    setCombo,
    addItem,
    removeItem,
    setItemAmount,
    clearShoppingCart,
  };
};
